import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ListingCard from "@/components/ListingCard";
import { Loading } from "@/components/auth/RequireAuth";
import { publicApi } from "@/lib/api";

const FILTERS = [
  { key: "duration_type", label: "Duration", opts: [["weekend", "Weekend"], ["full-day", "Full day"], ["multi-day", "Multi-day"]] },
  { key: "budget", label: "Budget", opts: [["affordable", "Up to PKR 40k"], ["mid", "PKR 40k–120k"], ["premium", "PKR 120k+"]] },
  { key: "audience", label: "Group", opts: [["public", "Public cohort"], ["women-only", "Women-only"], ["private", "Private buy-out"]] },
  { key: "region", label: "Region", opts: [["pakistan", "Pakistan"], ["outbound", "Outbound"]] },
];

function FilterSelect({ f, value, onChange }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(f.key, v === "all" ? "" : v)}>
      <SelectTrigger className="h-10 w-full rounded-full bg-white sm:w-[170px]" data-testid={`filter-${f.key}`}><SelectValue placeholder={f.label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Any {f.label.toLowerCase()}</SelectItem>
        {f.opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ vibes: [], destinations: [] });
  const [items, setItems] = useState(null);
  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const qs = params.toString();

  useEffect(() => { publicApi.get("/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    setItems(null);
    publicApi.get(`/listings?${qs}`).then((r) => setItems(r.data));
  }, [qs]);

  const vibe = params.get("vibe");
  const dateFrom = params.get("date_from");
  return (
    <div className="mx-auto max-w-7xl px-5 py-12 md:px-8" data-testid="explore-page">
      <div className="eyebrow text-secondary">Explore</div>
      <h1 className="mt-2 text-4xl tracking-tight text-primary sm:text-5xl">Every curated escape, in one place.</h1>
      <div className="mt-8 flex flex-wrap gap-2">
        <button onClick={() => set("vibe", "")} className={`rounded-full border px-4 py-2 text-sm transition-colors ${!vibe ? "border-primary bg-primary text-white" : "border-border bg-white hover:border-primary"}`} data-testid="vibe-chip-all">All vibes</button>
        {meta.vibes.map((v) => (
          <button key={v.key} onClick={() => set("vibe", v.key)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${vibe === v.key ? "border-secondary bg-secondary text-white" : "border-border bg-white hover:border-secondary"}`} data-testid={`vibe-chip-${v.key}`}>{v.name}</button>
        ))}
      </div>
      <div className="mt-4 flex flex-col flex-wrap gap-3 sm:flex-row">
        <Select value={params.get("destination") || "all"} onValueChange={(v) => set("destination", v === "all" ? "" : v)}>
          <SelectTrigger className="h-10 w-full rounded-full bg-white sm:w-[190px]" data-testid="filter-destination"><SelectValue placeholder="Destination" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any destination</SelectItem>
            {meta.destinations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        {FILTERS.map((f) => <FilterSelect key={f.key} f={f} value={params.get(f.key)} onChange={set} />)}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-10 justify-start rounded-full bg-white font-normal" data-testid="filter-date">
              <CalendarIcon className="mr-2 h-4 w-4" />{dateFrom ? `From ${format(new Date(dateFrom), "d MMM")}` : "Any date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={dateFrom ? new Date(dateFrom) : undefined} onSelect={(d) => set("date_from", d ? format(d, "yyyy-MM-dd") : "")} />
          </PopoverContent>
        </Popover>
        <Input type="number" min={1} max={20} placeholder="Group size" value={params.get("group_size") || ""} onChange={(e) => set("group_size", e.target.value)} className="h-10 w-full rounded-full bg-white sm:w-[130px]" data-testid="filter-group-size" />
        {qs && <Button variant="ghost" className="h-10 rounded-full" onClick={() => setParams({})} data-testid="filters-clear-btn"><X className="mr-1 h-4 w-4" />Clear</Button>}
      </div>
      {items === null ? <Loading /> : items.length === 0 ? (
        <div className="mt-16 rounded-2xl border border-dashed border-border p-12 text-center" data-testid="explore-empty">
          <p className="font-display text-2xl text-primary">Nothing matches that exactly.</p>
          <p className="mt-2 text-muted-foreground">We keep supply small and curated. Loosen a filter, or try the guided matcher.</p>
        </div>
      ) : (
        <>
          <p className="mt-8 text-sm text-muted-foreground" data-testid="explore-count">{items.length} getaway{items.length === 1 ? "" : "s"}</p>
          <div className="mt-6 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((l) => <ListingCard key={l.id} l={l} />)}
          </div>
        </>
      )}
    </div>
  );
}
